import React, { useState } from 'react';
import { Star, X, Sparkles, Send, Check, User, Tag, MessageSquare } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../config/api';
import { useLogo } from '../../contexts/LogoContext';

const RATING_LABELS = {
  1: 'Needs Improvement',
  2: 'Fair Experience',
  3: 'Good Experience',
  4: 'Great Experience',
  5: 'Outstanding Experience! 🎉'
};

const SUGGESTED_ROLES = [
  'Community Member',
  'Event Attendee',
  'Volunteer',
  'Music Student / Parent',
  'Supporter / Donor'
];

export default function FeedbackModal({ isOpen, onClose, onFeedbackSubmitted }) {
  const { logoUrl } = useLogo();
  const [rating, setRating] = useState(5);
  const [hoverRating, setHoverRating] = useState(0);
  const [name, setName] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [role, setRole] = useState('Community Member');
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [honeypot, setHoneypot] = useState(''); // Anti-bot honeypot

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!comment.trim()) {
      toast.error('Please enter your feedback comments.');
      return;
    }

    if (rating < 1 || rating > 5) {
      toast.error('Please select a rating between 1 and 5 stars.');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        name: isAnonymous ? 'Anonymous' : (name.trim() || 'Community Member'),
        role: role.trim() || 'Community Member',
        rating: Number(rating),
        comment: comment.trim(),
        website: honeypot
      };

      const res = await api.post('/feedback', payload);
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Failed to submit feedback');
      }

      setIsSuccess(true);
      toast.success('Thank you! Your feedback was submitted successfully.');
      if (onFeedbackSubmitted) {
        onFeedbackSubmitted(data.feedback);
      }
    } catch (err) {
      console.error('Feedback submit error:', err);
      toast.error(err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetAndClose = () => {
    setIsSuccess(false);
    setRating(5);
    setHoverRating(0);
    setName('');
    setRole('Community Member');
    setComment('');
    setIsAnonymous(false);
    onClose();
  };

  const currentDisplayRating = hoverRating || rating;

  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center p-2.5 sm:p-4 md:p-6 bg-black/60 backdrop-blur-sm overflow-y-auto animate-fadeIn"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleResetAndClose();
      }}
    >
      <div 
        className="relative w-full max-w-lg max-h-[85vh] sm:max-h-[88vh] flex flex-col bg-white rounded-2xl sm:rounded-3xl shadow-2xl border border-gray-100 overflow-hidden my-auto animate-scaleUp transform transition-all"
        role="dialog"
        aria-modal="true"
      >
        {/* Header with donation hero gradient banner */}
        <div className="relative shrink-0 bg-gradient-to-br from-slate-900 via-purple-950 to-orange-950 p-3.5 sm:p-5 text-white overflow-hidden border-b border-white/10">
          {/* Subtle background ambient glows matching donation hero */}
          <div className="absolute top-0 right-0 w-40 h-40 bg-purple-500/20 rounded-full blur-3xl pointer-events-none"></div>
          <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-orange-500/20 rounded-full blur-2xl pointer-events-none"></div>

          <button
            onClick={handleResetAndClose}
            className="absolute top-3 right-3 p-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white/80 hover:text-white transition-colors duration-200 focus:outline-none"
            aria-label="Close modal"
          >
            <X className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>

          <div className="flex items-center gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full overflow-hidden shrink-0 shadow-md ring-2 ring-white/30">
              <img
                src={logoUrl || '/logo512.png'}
                alt="BOW Logo"
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.currentTarget.src = '/logo512.png';
                }}
              />
            </div>
            <div className="min-w-0 flex-1 pr-6">
              <div className="flex items-center gap-1.5 text-amber-300 text-[11px] sm:text-xs font-semibold uppercase tracking-wider mb-0.5">
                <Sparkles className="w-3.5 h-3.5 text-amber-400" />
                <span>Community Voice</span>
              </div>
              <h2 className="text-lg sm:text-2xl font-bold tracking-tight text-white leading-tight">
                Share Your Experience
              </h2>
            </div>
          </div>
          <p className="text-white/80 text-[11px] sm:text-xs mt-2 max-w-md line-clamp-2">
            Your reviews help us grow, inspire others, and build stronger cultural programs for everyone!
          </p>
        </div>

        {/* Modal Body */}
        <div className="flex-1 overflow-y-auto p-3.5 sm:p-5">
          {isSuccess ? (
            <div className="text-center py-4 sm:py-6 space-y-3 sm:space-y-4">
              <div className="relative w-16 h-16 sm:w-20 sm:h-20 mx-auto">
                {/* Subtle ambient glow behind logo */}
                <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-orange-400 to-amber-500 blur-md opacity-40 animate-pulse"></div>
                
                {/* BOW Logo circular badge */}
                <div className="relative w-full h-full rounded-full bg-white p-2 shadow-xl border-2 border-orange-200 flex items-center justify-center overflow-hidden animate-bounce" style={{ animationDuration: '2.5s' }}>
                  <img
                    src={logoUrl || '/logo512.png'}
                    alt="Beats of Washington Logo"
                    className="w-full h-full object-contain"
                    onError={(e) => {
                      e.currentTarget.src = '/logo512.png';
                    }}
                  />
                </div>

                {/* Verified Green Check badge on bottom-right corner */}
                <div className="absolute -bottom-1 -right-1 w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-emerald-500 text-white flex items-center justify-center shadow-lg border-2 border-white">
                  <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4 stroke-[3]" />
                </div>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-900">
                Thank You for Your Feedback!
              </h3>
              <p className="text-xs sm:text-sm text-gray-600 max-w-md mx-auto leading-relaxed">
                Your feedback has been received and will appear on the homepage once reviewed by our team. We appreciate your support!
              </p>
              <div className="pt-2 sm:pt-3">
                <button
                  type="button"
                  onClick={handleResetAndClose}
                  className="px-5 py-2 rounded-full bg-gradient-to-r from-orange-500 to-amber-500 text-white font-semibold text-sm shadow-md hover:from-orange-600 hover:to-amber-600 transition-all duration-200 hover:scale-105"
                >
                  Close
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
              {/* Anti-spam honeypot (hidden from human visitors) */}
              <input
                type="text"
                name="website"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                tabIndex="-1"
                autoComplete="off"
                className="hidden"
                style={{ display: 'none' }}
                aria-hidden="true"
              />

              {/* Star Rating Section */}
              <div className="text-center bg-gradient-to-br from-amber-50/70 to-orange-50/60 p-2.5 sm:p-3 rounded-xl border border-amber-100">
                <label className="block text-[11px] sm:text-xs font-bold text-gray-800 uppercase tracking-wider mb-1">
                  Your Overall Rating
                </label>
                <div className="flex items-center justify-center gap-1 sm:gap-1.5 my-1">
                  {[1, 2, 3, 4, 5].map((star) => {
                    const isFilled = star <= currentDisplayRating;
                    return (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setRating(star)}
                        onMouseEnter={() => setHoverRating(star)}
                        onMouseLeave={() => setHoverRating(0)}
                        className="p-1 transition-transform duration-150 hover:scale-125 focus:outline-none group"
                        aria-label={`${star} star rating`}
                      >
                        <Star
                          className={`w-6 h-6 sm:w-8 sm:h-8 transition-colors duration-200 ${
                            isFilled
                              ? 'text-amber-400 fill-amber-400 drop-shadow-[0_2px_6px_rgba(251,191,36,0.5)]'
                              : 'text-gray-300 group-hover:text-amber-200'
                          }`}
                        />
                      </button>
                    );
                  })}
                </div>
                <div className="text-[11px] sm:text-xs font-semibold text-amber-700 min-h-[16px] transition-all">
                  {RATING_LABELS[currentDisplayRating] || ''}
                </div>
              </div>

              {/* Feedback Comment */}
              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1 flex items-center justify-between">
                  <span className="flex items-center gap-1.5">
                    <MessageSquare className="w-3.5 h-3.5 text-orange-500" />
                    Your Review & Comments <span className="text-red-500">*</span>
                  </span>
                  <span className="text-[10px] sm:text-[11px] text-gray-400 font-normal">
                    {comment.length}/500
                  </span>
                </label>
                <textarea
                  required
                  rows={3}
                  maxLength={500}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Share what you enjoyed, the impact of our events, workshops, or your message for Beats of Washington..."
                  className="w-full px-3 py-2 text-xs sm:text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all placeholder:text-gray-400 resize-none shadow-sm"
                />
              </div>

              {/* Name & Role Section */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1.5">
                    <User className="w-3.5 h-3.5 text-orange-500" />
                    Your Name
                  </label>
                  <input
                    type="text"
                    disabled={isAnonymous}
                    value={isAnonymous ? '' : name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={isAnonymous ? 'Anonymous' : 'e.g. John Doe'}
                    className={`w-full px-3 py-1.5 sm:py-2 text-xs sm:text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all ${
                      isAnonymous ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white'
                    }`}
                  />
                  <div className="mt-1 flex items-center">
                    <input
                      type="checkbox"
                      id="anonCheck"
                      checked={isAnonymous}
                      onChange={(e) => setIsAnonymous(e.target.checked)}
                      className="rounded border-gray-300 text-orange-500 focus:ring-orange-500 w-3.5 h-3.5"
                    />
                    <label htmlFor="anonCheck" className="ml-1.5 text-[11px] text-gray-500 cursor-pointer select-none">
                      Post as Anonymous
                    </label>
                  </div>
                </div>

                {/* Role / Tagline */}
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1.5">
                    <Tag className="w-3.5 h-3.5 text-orange-500" />
                    Who are you? (Optional)
                  </label>
                  <input
                    type="text"
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    placeholder="e.g. Event Attendee, Volunteer"
                    className="w-full px-3 py-1.5 sm:py-2 text-xs sm:text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                  />
                  {/* Quick role suggestions */}
                  <div className="mt-1 flex flex-wrap gap-1">
                    {SUGGESTED_ROLES.slice(0, 3).map((r) => (
                      <button
                        key={r}
                        type="button"
                        onClick={() => setRole(r)}
                        className={`text-[10px] px-2 py-0.5 rounded-full transition-colors ${
                          role === r
                            ? 'bg-orange-500 text-white font-medium'
                            : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
                        }`}
                      >
                        {r}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Submit Buttons */}
              <div className="pt-2 sm:pt-3 flex items-center justify-end gap-2.5 border-t border-gray-100">
                <button
                  type="button"
                  onClick={handleResetAndClose}
                  className="px-3.5 py-1.5 sm:py-2 text-xs sm:text-sm font-semibold text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="inline-flex items-center gap-1.5 px-5 py-2 sm:py-2.5 rounded-full bg-gradient-to-r from-orange-500 via-amber-500 to-red-500 text-white font-bold text-xs sm:text-sm shadow-md hover:from-orange-600 hover:to-red-600 transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Submitting...</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-3.5 h-3.5" />
                      <span>Submit Review</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

